/**
 * Redux slice for caching GameWord data (variations + gameplay info)
 * Eliminates duplicate pathfinding between makeMove and useHintSystem
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { GameWord, WordGameplayInfo } from '../Utils/GameTypes';
import { logger } from '@mental-mint/shared-utils';

/**
 * State shape: Map from onClickDestination → GameWord
 */
interface WordCacheState {
  cache: Record<string, GameWord>;
}

const initialState: WordCacheState = {
  cache: {}
};

const wordCacheSlice = createSlice({
  name: 'wordCache',
  initialState,
  reducers: {
    /**
     * Add or update a GameWord in the cache
     */
    setGameWord: (state, action: PayloadAction<{ word: string; gameWord: GameWord }>) => {
      const { word, gameWord } = action.payload;
      state.cache[word] = gameWord;
      logger.debug(`[WordCache] Cached GameWord: ${word}`, gameWord);
    },

    /**
     * Update gameplay info for an existing GameWord
     */
    updateGameplayInfo: (state, action: PayloadAction<{ word: string; gameplay: WordGameplayInfo }>) => {
      const { word, gameplay } = action.payload;
      if (state.cache[word]) {
        state.cache[word].gameplay = gameplay;
        logger.debug(`[WordCache] Updated gameplay for: ${word}`, gameplay);
      } else {
        logger.warn(`[WordCache] Attempted to update non-existent word: ${word}`);
      }
    },

    /**
     * Batch add multiple GameWords (for neighbor pre-caching)
     */
    setGameWords: (state, action: PayloadAction<GameWord[]>) => {
      action.payload.forEach(gw => {
        state.cache[gw.variations.onClickDestination] = gw;
      });
      logger.debug(`[WordCache] Batch cached ${action.payload.length} GameWords`);
    },

    /**
     * Clear entire cache (when loading new puzzle)
     */
    clearWordCache: (state) => {
      const count = Object.keys(state.cache).length;
      state.cache = {};
      logger.debug(`[WordCache] Cleared cache (${count} words removed)`);
    },

    /**
     * Remove specific word from cache
     */
    removeGameWord: (state, action: PayloadAction<string>) => {
      const word = action.payload;
      delete state.cache[word];
      logger.debug(`[WordCache] Removed word: ${word}`);
    }
  }
});

export const {
  setGameWord,
  updateGameplayInfo,
  setGameWords,
  clearWordCache,
  removeGameWord
} = wordCacheSlice.actions;

export default wordCacheSlice.reducer;

/**
 * Selectors
 */
export const selectGameWord = (state: { wordCache: WordCacheState }, word: string): GameWord | undefined => {
  return state.wordCache.cache[word];
};

export const selectAllGameWords = (state: { wordCache: WordCacheState }): GameWord[] => {
  return Object.values(state.wordCache.cache);
};

export const selectCacheSize = (state: { wordCache: WordCacheState }): number => {
  return Object.keys(state.wordCache.cache).length;
};
